import React, { useState } from "react";
import { Card } from "@/race-tracker/components/ui/card";
import { Badge } from "@/race-tracker/components/ui/badge";
import { Button } from "@/race-tracker/components/ui/button";
import { ChevronDown, ChevronRight, Star, Edit2, Plus, AlertTriangle } from "lucide-react";
import RaceRow from "./RaceRow";
import { getSweepStaleness } from "@/race-tracker/lib/sweepUtils";

export default function AdvertiserCard({ advertiser, races, onRaceClick, onEditAdvertiser, onAddRace }) {
  const [expanded, setExpanded] = useState(advertiser.status === "Active");
  const activeRaces = races.filter((r) => r.status !== "Done");
  const isDone = advertiser.status === "Done";
  const isInactive = advertiser.status === "Inactive";

  const hasOverdueSweep = activeRaces.some((r) =>
    getSweepStaleness(r.last_digital_sweep).level === "critical" ||
    getSweepStaleness(r.last_linear_sweep).level === "critical"
  );

  return (
    <Card className={`overflow-hidden transition-all duration-200 ${isDone ? "opacity-40" : isInactive ? "opacity-60" : ""}`}>
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-accent/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <button className="shrink-0 text-muted-foreground">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold truncate">{advertiser.advertiser_name}</h3>
            {advertiser.smg_plus && (
              <Badge variant="secondary" className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0 gap-0.5">
                <Star className="w-2.5 h-2.5 fill-amber-500" /> SMG+
              </Badge>
            )}
            {advertiser.agency && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
                {advertiser.agency}
              </Badge>
            )}
            <Badge
              variant={isDone || isInactive ? "outline" : "default"}
              className={`text-[10px] px-1.5 py-0 ${
                isDone ? "text-muted-foreground" :
                isInactive ? "text-amber-600 border-amber-300 bg-amber-50" :
                "bg-emerald-500/10 text-emerald-600 border-emerald-200"
              }`}
            >
              {advertiser.status}
            </Badge>
          </div>
          {advertiser.candidate_name && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {advertiser.candidate_name}
            </p>
          )}
        </div>

        {/* Chips */}
        <div className="hidden md:flex items-center gap-1.5 shrink-0 flex-wrap">
          {advertiser.data_analyst && (
            <Chip label="DA" value={advertiser.data_analyst} color="blue" />
          )}
          {advertiser.account_executive && (
            <Chip label="AE" value={advertiser.account_executive} color="purple" />
          )}
          {advertiser.account_manager && (
            <Chip label="AM" value={advertiser.account_manager} color="teal" />
          )}
          {hasOverdueSweep && !expanded && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md border bg-red-50 text-red-600 border-red-200">
              <AlertTriangle className="w-2.5 h-2.5" /> Overdue Sweep
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {activeRaces.length} race{activeRaces.length !== 1 ? "s" : ""}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => { e.stopPropagation(); onEditAdvertiser(advertiser); }}
          >
            <Edit2 className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Expanded Race List */}
      {expanded && races.length > 0 && (
        <div className="border-t border-border">
          <div className="px-4 py-1.5 bg-muted/50 grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_80px_80px_120px_60px] gap-2 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
            <span>State</span>
            <span>Level of Office</span>
            <span>District</span>
            <span>Team</span>
            <span>Election</span>
            <span>Date</span>
            <span>Dig. Sweep</span>
            <span>Lin. Sweep</span>
            <span>Markets</span>
            <span className="text-right">Ref.</span>
          </div>
          {races.map((race) => (
            <RaceRow key={race.id} race={race} onClick={() => onRaceClick(race)} />
          ))}
        </div>
      )}

      {expanded && (
        <div className={`border-t border-border px-4 py-2 ${races.length === 0 ? "py-4" : ""}`}>
          {races.length === 0 && (
            <p className="text-xs text-muted-foreground text-center mb-2">No races added yet</p>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="w-full h-7 text-xs text-muted-foreground hover:text-foreground gap-1"
            onClick={(e) => { e.stopPropagation(); onAddRace(advertiser.id); }}
          >
            <Plus className="w-3 h-3" /> Add Race
          </Button>
        </div>
      )}
    </Card>
  );
}

function Chip({ label, value, color }) {
  const colors = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    teal: "bg-teal-50 text-teal-700 border-teal-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md border ${colors[color]}`}>
      <span className="opacity-60">{label}</span> {value}
    </span>
  );
}